import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { loginCouple, clearError, setCurrentPartnerRole } from '../../store/slices/authSlice';
import { getCurrentPartnerRole, storeCoupleAuth } from '../../lib/CoupleAuthentication';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, Heart, Users } from 'lucide-react';
import { toast } from 'sonner';

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToSignup?: () => void;
}

export default function LoginForm({ onSuccess, onSwitchToSignup }: LoginFormProps) {
  const dispatch = useAppDispatch();
  const { isLoggingIn, error } = useAppSelector(state => state.auth);
  
  const [formData, setFormData] = useState({
    partner1_name: '',
    partner2_name: ''
  });

  const handleInputChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear any previous errors when user starts typing
    if (error) {
      dispatch(clearError());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.partner1_name.trim() || !formData.partner2_name.trim()) {
      toast.error('Please enter both partner names');
      return;
    }

    if (formData.partner1_name.trim().toLowerCase() === formData.partner2_name.trim().toLowerCase()) {
      toast.error('Partner names must be different');
      return;
    }

    try {
      const result = await dispatch(loginCouple({
        partner1_name: formData.partner1_name.trim(),
        partner2_name: formData.partner2_name.trim()
      })).unwrap();

      // Store authentication data in localStorage
      storeCoupleAuth(result);

      // Determine which partner is logging in and set their role
      const partnerRole = getCurrentPartnerRole(formData.partner1_name.trim());
      if (partnerRole) {
        dispatch(setCurrentPartnerRole(partnerRole));
      }

      toast.success('Successfully logged in! Welcome back!');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is already handled by the rejected case in authSlice
      console.error('Login failed:', error);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto" data-testid="login-form">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-primary/10 rounded-full">
            <Heart className="h-8 w-8 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl">Welcome Back</CardTitle>
        <CardDescription>
          Log in to your couple's expense tracking account
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive" data-testid="login-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="partner1_name">First Partner's Name</Label>
            <Input
              id="partner1_name"
              type="text"
              placeholder="Enter first partner's name"
              value={formData.partner1_name}
              onChange={handleInputChange('partner1_name')}
              disabled={isLoggingIn}
              data-testid="partner1-name-input"
              className="w-full"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="partner2_name">Second Partner's Name</Label>
            <Input
              id="partner2_name"
              type="text"
              placeholder="Enter second partner's name"
              value={formData.partner2_name}
              onChange={handleInputChange('partner2_name')}
              disabled={isLoggingIn}
              data-testid="partner2-name-input"
              className="w-full"
            />
          </div>
          
          <Button
            type="submit"
            className="w-full"
            disabled={isLoggingIn}
            data-testid="login-submit-button"
          >
            {isLoggingIn ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Logging in...
              </>
            ) : (
              <>
                <Users className="mr-2 h-4 w-4" />
                Log In
              </>
            )}
          </Button>
        </form>
        
        {onSwitchToSignup && (
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={onSwitchToSignup}
                className="text-primary hover:underline font-medium"
                data-testid="switch-to-signup-button"
              >
                Create one together
              </button>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}